import { useEffect, useState } from "react";
import { toNumber } from "../utils/commons";

export type SortOrder = 'asc' | 'desc';

export interface Sortable<T> {
  sortField: keyof T | undefined;
  sortOrder: SortOrder;
}

interface SortableTable<T> extends Sortable<T> {
  sortedData: T[];
  toggleSorting: (sortField: keyof T) => void
}

const useSortableTable = <T>(data: T[], presorted?: Sortable<T>): SortableTable<T> => {
  const [sortedData, setSortedData] = useState<T[]>(data);
  const [prevData, setPrevData] = useState<T[]>([]);

  const [sortField, setSortField] = useState(presorted?.sortField);
  const [sortOrder, setSortOrder] = useState<SortOrder>(presorted?.sortOrder ?? 'asc');

  useEffect(() => {
    if (JSON.stringify(prevData) !== JSON.stringify(data)) {
      setSortedData([...data]);
      setPrevData([...data]);
    }
  }, [data, prevData]);

  const handleSorting = (sortField: keyof T, sortOrder: SortOrder) => {
    if (!sortField) return;

    const sorted = [...data].sort((a: any, b: any) => {
      const displayOrderA = toNumber(a['displayOrder'], 0);
      const displayOrderB = toNumber(b['displayOrder'], 0);

      if (displayOrderA !== displayOrderB) {
        return displayOrderA < displayOrderB ? -1 : 1;
      }

      const valueA = String(a[sortField] ?? '');
      const valueB = String(b[sortField] ?? '');

      if (valueA === '' && valueB === '') return 0;
      if (valueA === '') return 1;
      if (valueB === '') return -1;

      // if sorting boolean values, true goes first for asc
      const booleanValues = ['true', 'false'];
      if (booleanValues.includes(valueA) && booleanValues.includes(valueB)) {
        return valueA.localeCompare(valueB) * (sortOrder === 'asc' ? -1 : 1);
      }

      return valueA.localeCompare(valueB, 'en', {
        numeric: true,
      }) * (sortOrder === 'asc' ? 1 : -1);
    });

    setSortedData(sorted);
  };

  const toggleSorting = (accessor: keyof T) => {
    const order = accessor === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(accessor);
    setSortOrder(order);
    handleSorting(accessor, order);
  };

  return { sortedData, sortField, sortOrder, toggleSorting };
};

export default useSortableTable;
